import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';

import { PaperCard } from 'components/Card';
import { CardAutocomplete } from 'components/Form';
import { FullPageStep } from 'components/Layout';
import { getCompanies } from 'features/companies/api/getAllCompanies';
import { useGetRelatedFrameworkAgreements } from 'features/companies/api/getRelatedFrameworkAgreements';
import { useGetRelatedDPS } from 'features/companies/api/getRelatedDPS';
import { useGetRelatedDirects } from 'features/companies/api/getRelatedDirects';
import { InstrumentGrid } from '../Buyer/InstrumentPicker';

const CompanyItem = ({ Name, VAT, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {Name} ({VAT})
    </Typography>
  </Stack>
);

const IntermediaryCard = ({ selected, onClick }) => (
  <PaperCard
    onClick={onClick}
    sx={{
      flex: 1,
      minWidth: 0,
      boxShadow: 0,
      borderRadius: 1,
      p: 2,
      cursor: 'pointer',
      borderColor: selected ? 'primary.main' : 'border.main',
      borderWidth: selected ? 2 : 1,
      borderStyle: 'solid',
    }}
  >
    <Typography variant="h2" fontSize="1.125rem" fontWeight={700}>
      Intermediary
    </Typography>
    <Typography variant="body2" color="text.secondary">
      A supplier posts the advertisement on behalf of the buyer.
    </Typography>
  </PaperCard>
);

export const AdminStartStep = ({ onCancel, onNext }) => {
  const [buyer, setBuyer] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [intermediarySelected, setIntermediarySelected] = useState(false);

  const buyerId = buyer?.company_id;
  const queryOptions = { enabled: !!buyerId };
  const { data: faData } = useGetRelatedFrameworkAgreements(buyerId, { limit: 100, queryOptions });
  const { data: dpsData } = useGetRelatedDPS(buyerId, { limit: 100, queryOptions });
  const { data: directsData } = useGetRelatedDirects(buyerId, { limit: 100, queryOptions });

  const items = buyerId
    ? [
        ...(faData?.frameworkAgreements || []).map((fa) => ({
          instrumentType: 'fa',
          id: fa.fa_id,
          name: fa.fa_name,
          refNumber: fa.ref_number,
          description: fa.Description,
        })),
        ...(dpsData?.dps || []).map((dps) => ({
          instrumentType: 'dps',
          id: dps.dps_id,
          name: dps.dps_name,
          refNumber: dps.ref_number,
          description: dps.Description,
        })),
        ...(directsData?.directs || []).map((direct) => ({
          instrumentType: 'direct',
          id: direct.direct_id,
          name: direct.direct_name,
          refNumber: direct.ref_number,
          description: direct.Description,
        })),
      ]
    : [];

  const handleBuyerChange = (value) => {
    setBuyer(value);
    setIntermediarySelected(false);
    setSupplier(null);
  };

  return (
    <FullPageStep onClose={onCancel}>
      <Stack gap={3} px={4} py={3} bgcolor="background.paper" borderTop="1px solid" borderColor="border.main">
        <Typography variant="h3">Add Advertisement</Typography>
        <CardAutocomplete
          name="adminStartBuyer"
          label="Select Buyer"
          queryFunction={(search) => getCompanies('buyer', 15, true, search)}
          optionLabelName="Name"
          ItemCard={CompanyItem}
          onChange={handleBuyerChange}
          skipCreatingShadow={true}
          fetchOnOpen={true}
          fullWidth
          wrapperSx={{ width: { xs: 1, md: 'calc((100% - 32px) / 3)' } }}
          loadingInitial
          changeSelectedValueOnChange={true}
        />
        {buyer && (
          <>
            <Typography variant="h4">Post as</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
              <IntermediaryCard
                selected={intermediarySelected}
                onClick={() => setIntermediarySelected(true)}
              />
              {[0, 1].map((index) => (
                <PaperCard
                  key={index}
                  sx={{ flex: 1, boxShadow: 0, borderRadius: 1, p: 2, visibility: 'hidden', display: { xs: 'none', md: 'flex' } }}
                />
              ))}
            </Stack>
            {intermediarySelected && (
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'flex-end' }} justifyContent="space-between">
                <CardAutocomplete
                  name="adminStartSupplier"
                  label="Select Supplier"
                  queryFunction={(search) => getCompanies('supplier', 15, false, search)}
                  optionLabelName="Name"
                  ItemCard={CompanyItem}
                  onChange={setSupplier}
                  skipCreatingShadow={true}
                  fetchOnOpen={true}
                  fullWidth
                  wrapperSx={{ width: { xs: 1, md: 'calc((100% - 32px) / 3)' } }}
                  loadingInitial
                  changeSelectedValueOnChange={true}
                />
                <Button
                  size="large"
                  variant="contained"
                  disabled={!supplier}
                  onClick={() => onNext({ mode: 'intermediary', buyer, supplier })}
                >
                  Continue
                </Button>
              </Stack>
            )}
            {items.length > 0 && (
              <InstrumentGrid
                items={items}
                logoUrl={buyer.square_logo_url}
                logoName={buyer.Name}
                onSelect={(item) =>
                  onNext({
                    mode: 'buyer',
                    buyer,
                    instrumentType: item.instrumentType,
                    instrumentId: item.id,
                    instrumentName: item.name,
                  })
                }
              />
            )}
          </>
        )}
      </Stack>
    </FullPageStep>
  );
};
